import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedinIn, FaTelegramPlane, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import { PERSON } from '../data';

const LINKS = [
  { Icon:FaEnvelope,       label:'Email',    value:PERSON.email,    href:`mailto:${PERSON.email}`, color:'#8b5cf6' },
  { Icon:FaLinkedinIn,     label:'LinkedIn', value:'linkedin.com/in/mustafa', href:PERSON.linkedin, color:'#0ea5e9' },
  { Icon:FaGithub,         label:'GitHub',   value:'github.com/mustafa',      href:PERSON.github,   color:'#e2e8f0' },
  { Icon:FaTelegramPlane,  label:'Telegram', value:'@mustafa_dev',            href:PERSON.telegram, color:'#22d3ee' },
  { Icon:FaWhatsapp,       label:'WhatsApp', value:'+251927505142',        href:PERSON.whatsapp, color:'#22c55e' },
];

const INPUT = `w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.07] rounded-xl text-white placeholder-slate-600 text-sm
  focus:outline-none focus:ring-2 focus:ring-violet-500/35 focus:border-violet-500/35 hover:border-white/[0.14] transition-all duration-200`;

export default function Contact() {
  const [form, setForm] = useState({ name:'', email:'', subject:'', message:'' });
  const [busy, setBusy] = useState(false);
  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const send = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields', {
        style:{ background:'#1a1a2e', color:'#e2e8f0', border:'1px solid rgba(239,68,68,0.3)' },
      });
      return;
    }
    setBusy(true);
    await new Promise(r => setTimeout(r, 1500)); // ← replace with emailjs.send(...)
    setBusy(false);
    toast.success("Message sent! I'll get back to you soon.", {
      duration:5000,
      style:{ background:'#1a1a2e', color:'#e2e8f0', border:'1px solid rgba(139,92,246,0.3)' },
    });
    setForm({ name:'', email:'', subject:'', message:'' });
  };

  return (
    <section id="contact" className="section-pad relative overflow-hidden">
      <Toaster position="bottom-right"/>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/8 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-violet-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div initial={{opacity:0,y:28}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.55}} viewport={{once:true}} className="text-center mb-16">
          <p className="text-violet-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">— Get In Touch</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Let's Build Something <span className="grad-text">Together</span>
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">
            Currently available for new projects. Let's discuss what you need.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">

          {/* Left — contact links */}
          <motion.div initial={{opacity:0,x:-28}} whileInView={{opacity:1,x:0}}
            transition={{duration:0.6}} viewport={{once:true}}
            className="lg:col-span-2 flex flex-col gap-4"
          >
            {/* Status card */}
            <div className="glass rounded-3xl p-7 border border-white/[0.06]">
              <h3 className="text-white font-bold text-lg mb-2">Open to opportunities</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Full-time roles, freelance projects, or consulting — let's talk.
              </p>
              <div className="flex items-center gap-2 mt-4">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
                <span className="text-green-400 text-sm font-medium">Available now</span>
              </div>
            </div>

            {/* Links */}
            {LINKS.map(({ Icon, label, value, href, color }, i) => (
              <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer"
                initial={{opacity:0,x:-16}} whileInView={{opacity:1,x:0}}
                transition={{delay:i*0.07}} viewport={{once:true}}
                whileHover={{x:5,scale:1.02}}
                className="flex items-center gap-4 glass border border-white/[0.06] hover:border-violet-500/25 rounded-2xl p-4 transition-all duration-200"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{backgroundColor:color+'20',color}}>
                  <Icon size={15}/>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">{label}</p>
                  <p className="text-white text-sm font-medium">{value}</p>
                </div>
              </motion.a>
            ))}
          </motion.div>

          {/* Right — form */}
          <motion.div initial={{opacity:0,x:28}} whileInView={{opacity:1,x:0}}
            transition={{duration:0.6,delay:0.1}} viewport={{once:true}}
            className="lg:col-span-3"
          >
            <form onSubmit={send} className="glass rounded-3xl p-8 border border-white/[0.06]">
              <h3 className="text-white font-bold text-xl mb-8">Send a Message</h3>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-slate-500 text-xs mb-1.5 block">Your Name *</label>
                  <motion.input whileFocus={{scale:1.01}} type="text" name="name"
                    value={form.name} onChange={set} placeholder="John Doe" className={INPUT}/>
                </div>
                <div>
                  <label className="text-slate-500 text-xs mb-1.5 block">Email Address *</label>
                  <motion.input whileFocus={{scale:1.01}} type="email" name="email"
                    value={form.email} onChange={set} placeholder="john@example.com" className={INPUT}/>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-slate-500 text-xs mb-1.5 block">Subject</label>
                <motion.input whileFocus={{scale:1.01}} type="text" name="subject"
                  value={form.subject} onChange={set} placeholder="Project inquiry, collaboration…" className={INPUT}/>
              </div>

              <div className="mb-8">
                <label className="text-slate-500 text-xs mb-1.5 block">Message *</label>
                <motion.textarea whileFocus={{scale:1.01}} name="message"
                  value={form.message} onChange={set} rows={6} placeholder="Tell me about your project…"
                  className={`${INPUT} resize-none`}/>
              </div>

              <motion.button type="submit" disabled={busy}
                whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                className="w-full py-4 font-bold text-white rounded-xl btn-primary neon-v flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <>
                    <motion.span animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:'linear'}}
                      className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full inline-block"/>
                    Sending…
                  </>
                ) : (
                  <><FaEnvelope size={13}/> Send Message</>
                )}
              </motion.button>

              <p className="text-slate-600 text-xs text-center mt-4">Typically responds within 24 hours</p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
